import React, { useState, useEffect } from 'react';
import { Switch } from '@mui/material';
import BootstrapForm from 'react-bootstrap/Form';
import UnitSearchDropdown from '../../molecules/UnitSearchDropdown/view';
import TaxSearchDropdown from '../../molecules/TaxSearchDropdown/view';
import ButtonLoading from '../../atoms/buttonLoading/view';
import './style.css';

/**
 * ServiceForm - Unified form for creating and editing services with premium split-layout.
 *
 * @param {Object} initialData - Initial data for the form (used in edit mode).
 * @param {Function} onSubmit - Callback function when the form is submitted.
 * @param {string} mode - 'create' or 'edit'.
 * @param {string} submitId - Unique ID for the submit button loading state.
 */
const ServiceForm = ({ initialData, onSubmit, mode = 'create', submitId }) => {
    const [formData, setFormData] = useState({
        sku: '',
        name: '',
        description: '',
        value: '',
        unit: '70',
        taxes_id: null,
        is_active: true
    });

    const [errors, setErrors] = useState({
        sku: false,
        name: false,
        value: false
    });

    const isEdit = mode === 'edit';

    useEffect(() => {
        if (initialData) {
            setFormData({
                sku: initialData.sku || '',
                name: initialData.name || '',
                description: initialData.description || '',
                value: initialData.value || '',
                unit: initialData.unit || '70',
                taxes_id: initialData.taxes_id || null,
                is_active: initialData.is_active !== undefined ? initialData.is_active : true
            });
        }
    }, [initialData]);

    const handleChange = (event) => {
        let { name, value, type, checked } = event.target;

        if (type === 'number') {
            value = value === '' ? '' : parseFloat(value);
        } else if (type === 'checkbox') {
            value = checked;
        }

        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));

        // Validate on change
        if (name === 'sku' || name === 'name') {
            setErrors(prev => ({ ...prev, [name]: value.trim() === '' }));
        }
        if (name === 'value') {
            const num = parseFloat(value);
            setErrors(prev => ({ ...prev, value: isNaN(num) || num <= 0 }));
        }
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        onSubmit(formData);
    };

    return (
        <div className="service-form-premium-container">
            <h2 className="service-form-premium-title">
                {isEdit ? 'Editar Servicio' : 'Crear Nuevo Servicio'}
            </h2>

            <BootstrapForm onSubmit={handleSubmit} id="service-unified-form" style={{ width: '100%', outline: 'none', display: 'block' }}>

                {/* Sección 1: Información Básica del Servicio */}
                <div className="service-form-section-split">
                    <div className="service-form-sidebar">
                        <h3 className="service-form-section-title">Información Básica</h3>
                        <p className="service-form-section-desc">Define el código de SKU único, el nombre público y la descripción detallada del servicio.</p>
                    </div>

                    <div className="service-form-right">
                        <div className="service-form-fields-row">
                            <div className="service-form-field-col">
                                <BootstrapForm.Group className="service-form-group-custom">
                                    <BootstrapForm.Label>SKU <span className="req">*</span></BootstrapForm.Label>
                                    <input
                                        className="service-form-input-premium"
                                        type="text"
                                        name="sku"
                                        value={formData.sku}
                                        onChange={handleChange}
                                        disabled={isEdit}
                                        placeholder="Ej: SERV-001"
                                        required
                                    />
                                    {errors.sku && <div className="service-form-error-text">El SKU es obligatorio.</div>}
                                </BootstrapForm.Group>
                            </div>
                            <div className="service-form-field-col">
                                <BootstrapForm.Group className="service-form-group-custom">
                                    <BootstrapForm.Label>Nombre del Servicio <span className="req">*</span></BootstrapForm.Label>
                                    <input
                                        className="service-form-input-premium"
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Nombre del servicio"
                                        required
                                    />
                                    {errors.name && <div className="service-form-error-text">El nombre es obligatorio.</div>}
                                </BootstrapForm.Group>
                            </div>
                        </div>

                        <div className="service-form-fields-row">
                            <div className="service-form-field-col service-form-full-width-col">
                                <BootstrapForm.Group className="service-form-group-custom">
                                    <BootstrapForm.Label>Descripción</BootstrapForm.Label>
                                    <textarea
                                        className="service-form-textarea-premium"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Describe brevemente el alcance del servicio..."
                                    />
                                </BootstrapForm.Group>
                            </div>
                        </div>
                    </div>
                </div>

                <hr className="service-form-separator" />

                {/* Sección 2: Configuración de Precios e Impuestos */}
                <div className="service-form-section-split">
                    <div className="service-form-sidebar">
                        <h3 className="service-form-section-title">Precios e Impuestos</h3>
                        <p className="service-form-section-desc">Establece el valor base de cobro, la unidad de medida y los impuestos que aplican.</p>
                    </div>

                    <div className="service-form-right">
                        <div className="service-form-fields-row">
                            <div className="service-form-field-col">
                                <BootstrapForm.Group className="service-form-group-custom">
                                    <BootstrapForm.Label>Impuesto asociado</BootstrapForm.Label>
                                    <TaxSearchDropdown
                                        selected={formData.taxes_id ? { tax_id: formData.taxes_id } : undefined}
                                        taxType={1}
                                        onSelected={(tax) => setFormData(prev => ({ ...prev, taxes_id: tax.id }))}
                                    />
                                </BootstrapForm.Group>
                            </div>
                            <div className="service-form-field-col">
                                <BootstrapForm.Group className="service-form-group-custom">
                                    <BootstrapForm.Label>Valor base <span className="req">*</span></BootstrapForm.Label>
                                    <input
                                        className="service-form-input-premium"
                                        type="number"
                                        name="value"
                                        value={formData.value}
                                        onChange={handleChange}
                                        placeholder="0.00"
                                        step="0.01"
                                        required
                                    />
                                    {errors.value && <div className="service-form-error-text">Número mayor que 0.</div>}
                                </BootstrapForm.Group>
                            </div>
                            <div className="service-form-field-col">
                                <BootstrapForm.Group className="service-form-group-custom">
                                    <BootstrapForm.Label>Unidad de medida</BootstrapForm.Label>
                                    <UnitSearchDropdown
                                        selected={formData.unit}
                                        onSelected={(key) =>
                                            setFormData(prev => ({ ...prev, unit: key }))
                                        }
                                    />
                                </BootstrapForm.Group>
                            </div>

                            {isEdit && (
                                <div className="service-form-field-col">
                                    <BootstrapForm.Group className="service-form-group-custom service-form-switch-premium">
                                        <BootstrapForm.Label>Estado del Servicio</BootstrapForm.Label>
                                        <div className="switch-wrapper">
                                            <span className="switch-status-label">{formData.is_active ? 'Activo' : 'Inactivo'}</span>
                                            <Switch
                                                checked={formData.is_active}
                                                onChange={handleChange}
                                                name="is_active"
                                                color="primary"
                                            />
                                        </div>
                                    </BootstrapForm.Group>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="service-form-footer-actions">
                    <ButtonLoading
                        id={submitId}
                        title={isEdit ? 'Guardar Cambios' : 'Crear Servicio'}
                        onClick={handleSubmit}
                        disabled={!formData.sku || !formData.name || !formData.value || errors.sku || errors.name || errors.value}
                    />
                </div>
            </BootstrapForm>
        </div>
    );
};

export default ServiceForm;
